import { useState, useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { streamWorkflowRun } from '@/api/workflowRuns'
import { useTenantKey } from './useTenantKey'
import { isActiveRunStatus } from '@/types'
import type { WorkflowRunDetail, WorkflowRunStreamEvent } from '@/types'

interface StreamState {
  isConnected: boolean
  isTerminal: boolean
  error: string | null
}

const TERMINAL_EVENTS = new Set(['run_completed', 'run_failed', 'run_cancelled'])
const APPROVAL_EVENTS = new Set(['phase_approved', 'phase_rejected', 'phase_awaiting_approval'])
const MAX_RECONNECT_ATTEMPTS = 3
const REFETCH_DEBOUNCE_MS = 1000
const FALLBACK_POLL_MS = 2000
const REFETCH_MAX_WAIT_MS = 4000

function deriveRunFields(snapshot: WorkflowRunDetail): WorkflowRunDetail {
  const completedPhases = snapshot.phases.filter(p => p.status === 'Completed').length
  const currentPhase = snapshot.phases.find(p => p.status === 'Running' || p.status === 'AwaitingApproval')
  const currentNode = snapshot.phases
    .flatMap(p => p.branches)
    .flatMap(b => b.nodes)
    .find(n => n.status === 'Running' || n.status === 'RetryPending' || n.status === 'Ready')

  return {
    ...snapshot,
    completedPhases,
    currentPhaseName: currentPhase?.name ?? null,
    currentNodeName: currentNode?.name ?? null,
  }
}

function patchSnapshot(snapshot: WorkflowRunDetail, event: WorkflowRunStreamEvent): WorkflowRunDetail {
  const { eventType, entityId, status } = event
  const metadata = (() => {
    if (!event.metadataJson) return null
    try {
      return JSON.parse(event.metadataJson) as {
        decidedBy?: string
        decidedByDisplayName?: string
        decidedAt?: string
        comment?: string
        errorSummary?: string
        errorMessage?: string
      }
    } catch {
      return null
    }
  })()

  if (eventType.startsWith('run_')) {
    return deriveRunFields({
      ...snapshot,
      status: status as WorkflowRunDetail['status'],
      errorSummary: metadata?.errorSummary ?? snapshot.errorSummary,
    })
  }

  if (eventType.startsWith('phase_')) {
    const updatedApprovals = (() => {
      if (eventType === 'phase_awaiting_approval') {
        const existing = snapshot.approvalRequests.find(a => a.phaseId === entityId)
        if (existing) {
          return snapshot.approvalRequests.map(a =>
            a.phaseId === entityId ? { ...a, status: 'Pending' as const } : a
          )
        }

        const phase = snapshot.phases.find(p => p.id === entityId)
        if (!phase) return snapshot.approvalRequests

        return [
          ...snapshot.approvalRequests,
          {
            id: `stream-${entityId}`,
            runId: snapshot.id,
            phaseId: entityId,
            phaseName: phase.name,
            message: phase.approvalMessage ?? '',
            status: 'Pending' as const,
            decidedBy: null,
            decidedAt: null,
            comment: null,
            approvalRoles: phase.approvalRoles ?? null,
          },
        ]
      }

      if (eventType === 'phase_approved' || eventType === 'phase_rejected') {
        const nextStatus = eventType === 'phase_approved' ? 'Approved' as const : 'Rejected' as const
        const existing = snapshot.approvalRequests.find(a => a.phaseId === entityId)
        if (existing) {
          return snapshot.approvalRequests.map(a =>
            a.phaseId === entityId
              ? {
                  ...a,
                  status: nextStatus,
                  decidedBy: metadata?.decidedByDisplayName ?? metadata?.decidedBy ?? a.decidedBy,
                  decidedAt: metadata?.decidedAt ?? a.decidedAt,
                  comment: metadata?.comment ?? a.comment,
                }
              : a
          )
        }

        const phase = snapshot.phases.find(p => p.id === entityId)
        if (!phase) return snapshot.approvalRequests

        return [
          ...snapshot.approvalRequests,
          {
            id: `stream-${entityId}`,
            runId: snapshot.id,
            phaseId: entityId,
            phaseName: phase.name,
            message: phase.approvalMessage ?? '',
            status: nextStatus,
            decidedBy: metadata?.decidedByDisplayName ?? metadata?.decidedBy ?? null,
            decidedAt: metadata?.decidedAt ?? null,
            comment: metadata?.comment ?? null,
            approvalRoles: phase.approvalRoles ?? null,
          },
        ]
      }

      return snapshot.approvalRequests
    })()

    const runStatus = (() => {
      if (eventType === 'phase_awaiting_approval') return 'AwaitingApproval' as const
      if (eventType === 'phase_approved' || eventType === 'phase_started') return 'Running' as const
      if (eventType === 'phase_rejected') return 'Failed' as const
      return snapshot.status
    })()

    const errorSummary = eventType === 'phase_rejected' && metadata?.comment
      ? metadata.comment
      : snapshot.errorSummary

    return deriveRunFields({
      ...snapshot,
      status: runStatus,
      errorSummary,
      approvalRequests: updatedApprovals,
      phases: snapshot.phases.map(p =>
        p.id === entityId ? { ...p, status: status as typeof p.status } : p
      ),
    })
  }

  if (eventType.startsWith('branch_')) {
    return deriveRunFields({
      ...snapshot,
      phases: snapshot.phases.map(p => ({
        ...p,
        branches: p.branches.map(b =>
          b.id === entityId ? { ...b } : b
        ),
      })),
    })
  }

  if (eventType.startsWith('node_')) {
    return deriveRunFields({
      ...snapshot,
      phases: snapshot.phases.map(p => ({
        ...p,
        branches: p.branches.map(b => ({
          ...b,
          nodes: b.nodes.map(n =>
            n.id === entityId ? { ...n, status: status as typeof n.status } : n
          ),
        })),
      })),
    })
  }

  return deriveRunFields(snapshot)
}

const initialState: StreamState = {
  isConnected: false,
  isTerminal: false,
  error: null,
}

export function useWorkflowRunStream(projectId: string, runId: string | undefined, enabled: boolean = true, reconnectKey: number = 0) {
  const [state, setState] = useState<StreamState>(initialState)
  const abortRef = useRef<AbortController | null>(null)
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  const retryCountRef = useRef(0)
  const terminalSeenRef = useRef(false)
  const lastEventIdRef = useRef<string | null>(null)
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refetchDueByRef = useRef<number | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const connectRef = useRef<() => void>(() => {})

  const refetchRun = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId, 'workflow-runs', runId!),
      exact: true,
    })
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId, 'workflow-runs', runId!, 'detail'),
      exact: true,
    })
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId, 'workflow-runs', runId!, 'nodes'),
    })
  }, [projectId, runId, queryClient, tenantKey])

  const cancelScheduledRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current)
    refetchTimerRef.current = null
    refetchDueByRef.current = null
  }, [])

  const scheduleRefetch = useCallback(() => {
    const now = Date.now()
    refetchDueByRef.current ??= now + REFETCH_MAX_WAIT_MS
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current)
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null
      refetchDueByRef.current = null
      refetchRun()
    }, Math.min(REFETCH_DEBOUNCE_MS, refetchDueByRef.current - now))
  }, [refetchRun])

  const invalidateRunLists = useCallback(() => {
    const runsKey = tenantKey('projects', projectId, 'workflow-runs')
    queryClient.invalidateQueries({
      queryKey: runsKey,
      predicate: query => typeof query.queryKey[runsKey.length] === 'object',
    })
  }, [projectId, queryClient, tenantKey])

  const clearTimers = useCallback(() => {
    cancelScheduledRefetch()
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    reconnectTimerRef.current = null
  }, [cancelScheduledRefetch])

  const connect = useCallback(() => {
    if (!projectId || !runId || !enabled) return

    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    reconnectTimerRef.current = null
    abortRef.current?.abort()
    const abortController = new AbortController()
    abortRef.current = abortController
    terminalSeenRef.current = false
    setState(current => ({ ...current, isTerminal: false, error: null }))
    const isCurrent = () => !abortController.signal.aborted && abortRef.current === abortController

    const runKey = tenantKey('projects', projectId, 'workflow-runs', runId)
    lastEventIdRef.current ??= queryClient.getQueryData<WorkflowRunDetail>(runKey)?.lastEventId ?? null

    const handleEnd = () => {
      if (!isCurrent()) return

      setState(current => ({ ...current, isConnected: false }))
      if (terminalSeenRef.current) return

      if (retryCountRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.pow(2, retryCountRef.current) * 1000
        retryCountRef.current++
        reconnectTimerRef.current = setTimeout(() => connectRef.current(), delay)
      } else {
        setState(current => ({ ...current, error: 'Connection lost. Please refresh the page.' }))
      }
    }

    streamWorkflowRun(
      projectId,
      runId,
      (eventType, data, id) => {
        if (!isCurrent()) return
        retryCountRef.current = 0
        if (id !== undefined) lastEventIdRef.current = id

        if (eventType === 'heartbeat') return

        if (eventType === 'resync') {
          scheduleRefetch()
          return
        }

        let patched: WorkflowRunDetail | undefined
        try {
          const event = JSON.parse(data) as WorkflowRunStreamEvent
          patched = queryClient.setQueryData<WorkflowRunDetail>(runKey, run => (run ? patchSnapshot(run, event) : run))
        } catch {
          console.error('Failed to parse workflow run stream event:', eventType)
        }

        if (patched && !isActiveRunStatus(patched.status)) {
          cancelScheduledRefetch()
          refetchRun()
        } else {
          scheduleRefetch()
        }

        if (TERMINAL_EVENTS.has(eventType)) {
          terminalSeenRef.current = true
          setState(current => ({ ...current, isTerminal: true, isConnected: false }))
        }

        if (TERMINAL_EVENTS.has(eventType) || APPROVAL_EVENTS.has(eventType)) {
          invalidateRunLists()
        }
      },
      abortController.signal,
      lastEventIdRef.current ?? undefined,
      () => {
        if (!isCurrent()) return
        setState({ isConnected: true, isTerminal: false, error: null })
      },
    ).then(handleEnd).catch((err) => {
      if (err instanceof Error && err.name === 'AbortError') return
      handleEnd()
    })
  }, [projectId, runId, enabled, queryClient, tenantKey, refetchRun, scheduleRefetch, cancelScheduledRefetch, invalidateRunLists])

  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  useEffect(() => {
    setState(initialState)
  }, [runId])

  useEffect(() => {
    if (!enabled || !runId) return

    lastEventIdRef.current = null
    retryCountRef.current = 0
    connect()

    return () => {
      abortRef.current?.abort()
      abortRef.current = null
      clearTimers()
    }
  }, [connect, clearTimers, enabled, reconnectKey, runId])

  useEffect(() => {
    if (!enabled || !state.error || state.isTerminal) return

    const interval = setInterval(refetchRun, FALLBACK_POLL_MS)
    return () => clearInterval(interval)
  }, [enabled, state.error, state.isTerminal, refetchRun])

  return state
}
